import configureMeasurements from 'convert-units';
import _ from 'lodash';
import { COLOR_OPTIONS } from 'types';
import {
  dimensionClassBaseUnit,
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

/**
 * Extracts the numeric value from a temperature string that may include units and symbols
 * @param value A string like "-55.0 °C" or "85.0 °C" or "155°C"
 * @returns The numeric value as a number, or null if no valid number found
 */
export const extractNumberFromTemperature = (
  value: string | null
): number | null => {
  if (!value) return null;

  // Match optional negative sign, digits, optional decimal point and digits
  const match = value.match(/-?\d+\.?\d*/);

  if (!match) return null;

  return parseFloat(match[0]);
};

/**
 * Returns a status color based on temperature ranges
 * @param temp The temperature value to evaluate
 * @returns COLOR_OPTIONS enum value based on temperature ranges
 */
export const getTemperatureStatus = (
  temp: number,
  specId: string
): COLOR_OPTIONS => {
  if (specId === 'maxoperatingtemperature') {
    if (temp <= 85) return COLOR_OPTIONS.error;
    if (temp <= 125) return COLOR_OPTIONS.warning;
    if (temp <= 154) return COLOR_OPTIONS.secondary;
    return COLOR_OPTIONS.success;
  }

  if (specId === 'minoperatingtemperature') {
    if (temp >= -20) return COLOR_OPTIONS.error;
    if (temp >= -40) return COLOR_OPTIONS.warning;
    if (temp >= -50) return COLOR_OPTIONS.secondary;
    if (temp >= -55) return COLOR_OPTIONS.primary;
    return COLOR_OPTIONS.success;
  }

  return COLOR_OPTIONS.primary;
};

const quantity = {
  systems: {
    metric: {
      ea: {
        name: {
          singular: 'Each',
          plural: 'Each',
        },
        to_anchor: 1,
      },
      pc: {
        name: {
          singular: 'Piece',
          plural: 'Piece',
        },
        to_anchor: 1,
      },
      pr: {
        name: {
          singular: 'Pair',
          plural: 'Pair',
        },
        to_anchor: 1,
      },
    },
  },
};

// Definitions copied from convert-units/lib/esm/definitions/*.js because direct imports are blocked
//  on changing the tsconfig moduleResolution to something modern
function getMass() {
  const metric = {
    mcg: {
      name: {
        singular: 'Microgram',
        plural: 'Micrograms',
      },
      to_anchor: {
        numerator: 1,
        denominator: 1e6,
      },
    },
    mg: {
      name: {
        singular: 'Milligram',
        plural: 'Milligrams',
      },
      to_anchor: {
        numerator: 1,
        denominator: 1e3,
      },
    },
    g: {
      name: {
        singular: 'Gram',
        plural: 'Grams',
      },
      to_anchor: 1,
    },
    kg: {
      name: {
        singular: 'Kilogram',
        plural: 'Kilograms',
      },
      to_anchor: 1e3,
    },
    mt: {
      name: {
        singular: 'Metric Tonne',
        plural: 'Metric Tonnes',
      },
      to_anchor: 1e6,
    },
  };
  const imperial = {
    oz: {
      name: {
        singular: 'Ounce',
        plural: 'Ounces',
      },
      to_anchor: {
        numerator: 1,
        denominator: 16,
      },
    },
    lb: {
      name: {
        singular: 'Pound',
        plural: 'Pounds',
      },
      to_anchor: 1,
    },
    st: {
      name: {
        singular: 'Stone',
        plural: 'Stones',
      },
      to_anchor: 14,
    },
    t: {
      name: {
        singular: 'Ton',
        plural: 'Tons',
      },
      to_anchor: 2e3,
    },
  };
  return {
    systems: {
      metric,
      imperial,
    },
    anchors: {
      metric: {
        imperial: {
          ratio: {
            numerator: 1,
            denominator: 453.59237,
          },
        },
      },
      imperial: {
        metric: {
          ratio: 453.59237,
        },
      },
    },
  };
}

function getLength() {
  const metric = {
    nm: {
      name: {
        singular: 'Nanometer',
        plural: 'Nanometers',
      },
      to_anchor: 1e-9,
    },
    μm: {
      name: {
        singular: 'Micrometer',
        plural: 'Micrometers',
      },
      to_anchor: 1e-6,
    },
    mm: {
      name: {
        singular: 'Millimeter',
        plural: 'Millimeters',
      },
      to_anchor: 1e-3,
    },
    cm: {
      name: {
        singular: 'Centimeter',
        plural: 'Centimeters',
      },
      to_anchor: 1e-2,
    },
    dm: {
      name: {
        singular: 'Decimeter',
        plural: 'Decimeters',
      },
      to_anchor: 1e-1,
    },
    m: {
      name: {
        singular: 'Meter',
        plural: 'Meters',
      },
      to_anchor: 1,
    },
    km: {
      name: {
        singular: 'Kilometer',
        plural: 'Kilometers',
      },
      to_anchor: 1e3,
    },
  };
  const imperial = {
    mil: {
      name: {
        singular: 'Mil',
        plural: 'Mils',
      },
      to_anchor: {
        numerator: 1,
        denominator: 12000,
      },
    },
    in: {
      name: {
        singular: 'Inch',
        plural: 'Inches',
      },
      to_anchor: {
        numerator: 1,
        denominator: 12,
      },
    },
    yd: {
      name: {
        singular: 'Yard',
        plural: 'Yards',
      },
      to_anchor: 3,
    },
    'ft-us': {
      name: {
        singular: 'US Survey Foot',
        plural: 'US Survey Feet',
      },
      to_anchor: 1.000002,
    },
    ft: {
      name: {
        singular: 'Foot',
        plural: 'Feet',
      },
      to_anchor: 1,
    },
    fathom: {
      name: {
        singular: 'Fathom',
        plural: 'Fathoms',
      },
      to_anchor: 6,
    },
    mi: {
      name: {
        singular: 'Mile',
        plural: 'Miles',
      },
      to_anchor: 5280,
    },
    nMi: {
      name: {
        singular: 'Nautical Mile',
        plural: 'Nautical Miles',
      },
      to_anchor: 6076.12,
    },
  };
  return {
    systems: {
      metric,
      imperial,
    },
    anchors: {
      metric: {
        imperial: {
          ratio: 3.28084,
        },
      },
      imperial: {
        metric: {
          ratio: {
            numerator: 1,
            denominator: 3.28084,
          },
        },
      },
    },
  };
}

function getVolume() {
  const metric = {
    mm3: {
      name: {
        singular: 'Cubic Millimeter',
        plural: 'Cubic Millimeters',
      },
      to_anchor: {
        numerator: 1,
        denominator: 1e6,
      },
    },
    cm3: {
      name: {
        singular: 'Cubic Centimeter',
        plural: 'Cubic Centimeters',
      },
      to_anchor: {
        numerator: 1,
        denominator: 1e3,
      },
    },
    dm3: {
      name: {
        singular: 'Cubic Decimeter',
        plural: 'Cubic Decimeters',
      },
      to_anchor: 1,
    },
    ml: {
      name: {
        singular: 'Millilitre',
        plural: 'Millilitres',
      },
      to_anchor: {
        numerator: 1,
        denominator: 1e3,
      },
    },
    cl: {
      name: {
        singular: 'Centilitre',
        plural: 'Centilitres',
      },
      to_anchor: {
        numerator: 1,
        denominator: 100,
      },
    },
    dl: {
      name: {
        singular: 'Decilitre',
        plural: 'Decilitres',
      },
      to_anchor: {
        numerator: 1,
        denominator: 10,
      },
    },
    l: {
      name: {
        singular: 'Litre',
        plural: 'Litres',
      },
      to_anchor: 1,
    },
    kl: {
      name: {
        singular: 'Kilolitre',
        plural: 'Kilolitres',
      },
      to_anchor: 1e3,
    },
    Ml: {
      name: {
        singular: 'Megalitre',
        plural: 'Megalitres',
      },
      to_anchor: 1e6,
    },
    Gl: {
      name: {
        singular: 'Gigalitre',
        plural: 'Gigalitres',
      },
      to_anchor: 1e9,
    },
    m3: {
      name: {
        singular: 'Cubic meter',
        plural: 'Cubic meters',
      },
      to_anchor: 1e3,
    },
    km3: {
      name: {
        singular: 'Cubic kilometer',
        plural: 'Cubic kilometers',
      },
      to_anchor: 1e12,
    },
    // Swedish units
    krm: {
      name: {
        singular: 'Kryddmått',
        plural: 'Kryddmått',
      },
      to_anchor: {
        numerator: 1,
        denominator: 1e3,
      },
    },
    tsk: {
      name: {
        singular: 'Tesked',
        plural: 'Teskedar',
      },
      to_anchor: {
        numerator: 5,
        denominator: 1e3,
      },
    },
    msk: {
      name: {
        singular: 'Matsked',
        plural: 'Matskedar',
      },
      to_anchor: {
        numerator: 15,
        denominator: 1e3,
      },
    },
    kkp: {
      name: {
        singular: 'Kaffekopp',
        plural: 'Kaffekoppar',
      },
      to_anchor: {
        numerator: 150,
        denominator: 1e3,
      },
    },
    glas: {
      name: {
        singular: 'Glas',
        plural: 'Glas',
      },
      to_anchor: {
        numerator: 200,
        denominator: 1e3,
      },
    },
    kanna: {
      name: {
        singular: 'Kanna',
        plural: 'Kannor',
      },
      to_anchor: 2.617,
    },
  };
  const imperial = {
    tsp: {
      name: {
        singular: 'Teaspoon',
        plural: 'Teaspoons',
      },
      to_anchor: {
        numerator: 1,
        denominator: 6,
      },
    },
    Tbs: {
      name: {
        singular: 'Tablespoon',
        plural: 'Tablespoons',
      },
      to_anchor: {
        numerator: 1,
        denominator: 2,
      },
    },
    in3: {
      name: {
        singular: 'Cubic inch',
        plural: 'Cubic inches',
      },
      to_anchor: 0.55411,
    },
    'fl-oz': {
      name: {
        singular: 'Fluid Ounce',
        plural: 'Fluid Ounces',
      },
      to_anchor: 1,
    },
    cup: {
      name: {
        singular: 'Cup',
        plural: 'Cups',
      },
      to_anchor: 8,
    },
    pnt: {
      name: {
        singular: 'Pint',
        plural: 'Pints',
      },
      to_anchor: 16,
    },
    qt: {
      name: {
        singular: 'Quart',
        plural: 'Quarts',
      },
      to_anchor: 32,
    },
    gal: {
      name: {
        singular: 'Gallon',
        plural: 'Gallons',
      },
      to_anchor: 128,
    },
    ft3: {
      name: {
        singular: 'Cubic foot',
        plural: 'Cubic feet',
      },
      to_anchor: 957.506,
    },
    yd3: {
      name: {
        singular: 'Cubic yard',
        plural: 'Cubic yards',
      },
      to_anchor: 25852.7,
    },
  };
  return {
    systems: {
      metric,
      imperial,
    },
    anchors: {
      metric: {
        imperial: {
          ratio: 33.8140226,
        },
      },
      imperial: {
        metric: {
          ratio: {
            numerator: 1,
            denominator: 33.8140226,
          },
        },
      },
    },
  };
}

// The DefinitelyTyped definitions haven't kept up with the actual code.
// Alias existing units to match our preferred abbreviations.
const volume = getVolume();
_.set(volume, 'systems.metric.m³', volume.systems?.metric?.m3);
_.set(volume, 'systems.metric.cc', volume.systems?.metric?.cm3);

export const convert = configureMeasurements({
  volume,
  weight: getMass(),
  length: getLength(),
  quantity,
});

export const convertToPreferred = (
  quant: number,
  dimensionUnit: UnitOfMeasureOptions
) => {
  const dimensionClass = UnitOfMeasureDetails[dimensionUnit].dimensionClass;
  const preferredUnit = dimensionClassBaseUnit[dimensionClass];
  const fromAbbreviation = UnitOfMeasureDetails[dimensionUnit].abbreviation;
  const toAbbreviation = UnitOfMeasureDetails[preferredUnit].abbreviation;
  return convert(quant).from(fromAbbreviation).to(toAbbreviation);
};

export const convertFromPreferred = (
  quant: number,
  dimensionUnit: UnitOfMeasureOptions
) => {
  const dimensionClass = UnitOfMeasureDetails[dimensionUnit].dimensionClass;
  const preferredUnit = dimensionClassBaseUnit[dimensionClass];
  const fromAbbreviation = UnitOfMeasureDetails[preferredUnit].abbreviation;
  const toAbbreviation = UnitOfMeasureDetails[dimensionUnit].abbreviation;
  return convert(quant).from(fromAbbreviation).to(toAbbreviation);
};

export const formatUoMQuant = (
  quant: number,
  dimensionUnit: UnitOfMeasureOptions
) => {
  const dimensionClass = UnitOfMeasureDetails[dimensionUnit].dimensionClass;
  const preferredUnit = dimensionClassBaseUnit[dimensionClass];
  const fromAbbreviation = UnitOfMeasureDetails[preferredUnit].abbreviation;
  const toAbbreviation = UnitOfMeasureDetails[dimensionUnit].abbreviation;
  const value = convert(quant).from(fromAbbreviation).to(toAbbreviation);
  const unitOfMeasure = UnitOfMeasureDetails[dimensionUnit];
  return `${_.toNumber(value).toLocaleString('en-US')} ${
    unitOfMeasure.abbreviation
  }`;
};
